import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './AddressModal.css';

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  title?: string;
  description?: string;
  canSkip?: boolean;
  parentEmail?: string; // Optional - save address to specific parent instead of current user
}

const AddressModal: React.FC<AddressModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  title = "Add Your Address",
  description = "Help us personalize your experience by adding your address information.",
  canSkip = false,
  parentEmail
}) => {
  const [addressLine1, setAddressLine1] = useState('');
  const [townCity, setTownCity] = useState('');
  const [stateProvinceCountry, setStateProvinceCountry] = useState('');
  const [postCode, setPostCode] = useState('');
  const [saving, setSaving] = useState(false);

  const apiService = ApiService.getInstance();
  const { pendingUserData, user, pendingToken, refreshUser } = useAuth();

  // Debug logging

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setAddressLine1('');
      setTownCity('');
      setStateProvinceCountry('');
      setPostCode('');
    }
  }, [isOpen]);

  const handleSave = async () => {
    // Validate required fields
    if (!townCity.trim()) {
      alert('Please enter your town/city - this helps us suggest local activities.');
      return;
    }

    setSaving(true);
    try {
      if (parentEmail) {
        // Save address to specific parent

        // First, get all parents to find the UUID by email
        const parentsResponse = await apiService.getParents();
        if (!parentsResponse.success || !parentsResponse.data) {
          throw new Error('Failed to load parents list');
        }

        const targetParent = parentsResponse.data.find((parent: any) => parent.email === parentEmail);
        if (!targetParent) {
          throw new Error(`Parent with email ${parentEmail} not found`);
        }

        // Update the parent's address
        const response = await apiService.updateParent(targetParent.uuid, {
          username: targetParent.username,
          email: targetParent.email,
          phone: targetParent.phone,
          role: targetParent.role,
          address_line_1: addressLine1.trim(),
          town_city: townCity.trim(),
          state_province_country: stateProvinceCountry.trim(),
          post_code: postCode.trim()
        });

        if (response.success) {
          onSaved();
        } else {
          console.error('❌ AddressModal: Failed to save parent address:', response.error);
          alert(`Failed to save address: ${response.error || 'Unknown error'}`);
        }
      } else {
        // Save address to current user (original behavior)
        const currentUser = pendingUserData || user;
        if (!currentUser) {
          throw new Error('No user data available');
        }


      // Ensure API service has the correct token if we're in pending registration
      if (pendingToken && pendingUserData) {
        apiService.setToken(pendingToken);
      }

      // Pass the user data from registration along with address fields
      const response = await apiService.updateProfile({
        username: currentUser.username || currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        address_line_1: addressLine1.trim(),
        town_city: townCity.trim(),
        state_province_country: stateProvinceCountry.trim(),
        post_code: postCode.trim()
      });


        if (response.success) {
          // Refresh user data to include the new address
          if (refreshUser) {
            await refreshUser();
          }
          onSaved();
        } else {
          console.error('❌ AddressModal: Failed to save address:', response.error);
          alert(`Failed to save address: ${response.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('❌ AddressModal: Error saving address:', error);
      alert(`Failed to save address: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (canSkip) {
      onSaved(); // Proceed without saving
    }
  };

  if (!isOpen) return null;

  return (
    <div className="address-modal-overlay">
      <div className="address-modal">
        <div className="address-modal-header">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>

        <form className="address-modal-content" autoComplete="on" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div className="form-group">
            <label htmlFor="address-line1">Address Line 1</label>
            <input
              id="address-line1"
              name="address1"
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              className="form-input"
              placeholder="Street address"
              autoComplete="address-line1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="city">Town/City *</label>
            <input
              id="city"
              name="city"
              type="text"
              value={townCity}
              onChange={(e) => setTownCity(e.target.value)}
              className="form-input"
              placeholder="Town or city"
              required
              autoComplete="address-level2"
            />
          </div>

          <div className="form-group">
            <label htmlFor="state">State/Province/County</label>
            <input
              id="state"
              name="region"
              type="text"
              value={stateProvinceCountry}
              onChange={(e) => setStateProvinceCountry(e.target.value)}
              className="form-input"
              placeholder="State, province, or county"
              autoComplete="address-level1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="postal-code">Post Code</label>
            <input
              id="postal-code"
              name="postalCode"
              type="text"
              value={postCode}
              onChange={(e) => setPostCode(e.target.value)}
              className="form-input"
              placeholder="Post code"
              autoComplete="postal-code"
            />
          </div>

          <div className="address-modal-note">
            <p>💡 Your town/city will be used to suggest local activities when you create new events.</p>
          </div>
        </form>

        <div className="address-modal-actions">
          {canSkip && (
            <button
              onClick={handleSkip}
              className="skip-btn"
              disabled={saving}
            >
              Skip for now
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="save-btn"
          >
            {saving ? 'Saving...' : 'Save Address'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressModal;